import { trackClientEvent } from 'analytics/analytics'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import Usps from 'components/Common/Usps/Usps'
import UspTile from 'components/Common/UspTile/UspTile'
import config from 'constants/config'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import getStaticPagesByType from 'lib/utils/getStaticPagesByType'
import { memo, useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import getHomepageUspsContent from 'selectors/frontPage/getHomepageUspsContent'

const DEFAULT_ABOUT_US_PATH = '/about-us'

function getAboutUsNavigation(page: App.PageConfig | undefined) {
  if (!page) return undefined

  const link = page.url ?? config.contentLinks.aboutUs
  const target = page.url?.target ?? '_blank'

  if (link?.href) {
    return { href: link.href, target }
  }

  return {
    to: link?.to ?? DEFAULT_ABOUT_US_PATH,
    target,
  }
}

function HomepageUsps() {
  const intl = useIntl()
  const uspItems = useAppSelector(getHomepageUspsContent)
  const showModal = useContext(ModalContext)
  const aboutUsPage = useMemo(() => getStaticPagesByType(['aboutUs'])[0], [])

  const handleLearnMoreClick = useCallback(() => {
    trackClientEvent({
      subject: 'learn_about_us',
      action: 'clicked',
      category: 'usp_about_us',
      type: 'interaction',
    })
  }, [])

  const handleSignupClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const headingText = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'rdcMYMPvCGI_HgjU7TjF',
        defaultMessage: 'Exclusive prices and <italic>more</italic> with every booking',
        description: 'Front page USP section heading in the booking or order summary view',
      },
      {
        italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      },
    )
  }, [intl])

  const learnMoreText = intl.formatMessage({
    id: '_avGsqleTmNtxMdcZ0yP',
    defaultMessage: 'Learn more about us',
    description: 'Learn more about us button text in the usp section',
  })

  const signUpText = intl.formatMessage({
    id: '0dj_CoePhvKb0DtHAkwf',
    defaultMessage: 'Sign up and save',
    description: 'Button text to sign up for an account and access exclusive member savings and deals',
  })

  const primaryActionButtonProps = useMemo(() => ({
    children: signUpText,
    onClick: handleSignupClick,
  }), [handleSignupClick, signUpText])

  const secondaryActionButtonProps = useMemo(() => {
    const aboutUsNavigation = getAboutUsNavigation(aboutUsPage)
    if (!aboutUsNavigation) {
      return undefined
    }
    return {
      children: learnMoreText,
      onClick: handleLearnMoreClick,
      ...aboutUsNavigation,
    }
  }, [aboutUsPage, handleLearnMoreClick, learnMoreText])

  return <Usps
    background="light-background"
    heading={headingText}
    primaryActionButtonProps={primaryActionButtonProps}
    secondaryActionButtonProps={secondaryActionButtonProps}
  >
    {uspItems.map(item => <UspTile
      key={item.bodyMessage.id}
      icon={<item.Icon />}
    >
      {intl.formatMessage(item.bodyMessage)}
    </UspTile>)}
  </Usps>
}

export default memo(HomepageUsps)
